import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { fetchGraphQLQuery, useGraphQLMutation } from '@/graphql/adapters';
import Head from 'next/head';
import styles from '@/styles/Home.module.css';
import { RegisterForm } from '@/components/form/RegisterForm';
import { getSeminarsQueryDocument } from '@/graphql/documents/getSeminars';
import { registerParticipantMutationDocument } from '@/graphql/documents/registerParticipant';
import { Block } from '@/components/form/Block';
import Image from 'next/image';
import { Box, Typography, CircularProgress, Stack } from '@mui/material';
import VysEduLogo from '@public/vys-edu.png';
import { formatDateWithTime } from 'date-formatter';
import { useRouter } from 'next/router';
import { GetSeminarsQuery, SeminarType } from '@/graphql/codegen/graphql';
import { RegistrationSuccessful } from '@/components/RegistrationSuccessful';
import { toast } from 'sonner';
import { SeminarCapacityFull } from '@/components/SeminarCapacityFull';
import { RegisterFormSchema } from '@/components/form/register-form-schema';

const SeminarRegistration: React.FC = () => {
  const router = useRouter();
  const { id } = router.query;

  const [seminar, setSeminar] = useState<
    GetSeminarsQuery['seminars'][0] | null
  >(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [participantEmail, setParticipantEmail] = useState('');

  // Fetch seminar data when component mounts or id changes
  useEffect(() => {
    const fetchSeminar = async () => {
      if (!id || Array.isArray(id)) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const { seminars } = await fetchGraphQLQuery(getSeminarsQueryDocument, {
          seminar: {
            seminarId: Number(id),
          },
        });

        if (seminars && seminars.length > 0) {
          setSeminar(seminars[0]);
        } else {
          setError('Seminář nebyl nalezen');
        }
      } catch (err) {
        console.error('Error fetching seminar:', err);
        setError('Nepodařilo se načíst seminář');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSeminar();
  }, [id]);

  const { mutate, isLoading: isRegisterSubmitLoading } = useGraphQLMutation(
    registerParticipantMutationDocument,
    {
      onSuccess: (result) => {
        setIsRegistered(true);
        if (result.register.email) {
          setParticipantEmail(result.register.email);
        }
      },
      onError: (untypedError, values) => {
        const error = untypedError as { message: string };
        if (!error.message) {
          throw new Error('Error does not inclued message attribute');
        }
        if (
          error.message.includes(
            'Participant with this email and name already exists',
          )
        ) {
          toast.error(
            `Účastník ${values.participant.name} s emailem ${values.participant.email} je již na tento seminář zaregistrovaný.`,
          );
        } else if (error.message.includes('Seminar is full')) {
          toast.error(
            `Vaše registrace nebyla provedena. Seminář už je plně obsazen.`,
          );
        }
      },
    },
  );

  const submit = useCallback(
    async (values: RegisterFormSchema) => {
      if (!seminar) return;

      if (values.name.includes('testerror')) {
        throw new Error('This is test error');
      }

      mutate({
        participant: {
          seminarId: seminar.id,
          email: values.email,
          name: values.name,
          phone: values.phone,
          address: "address" in values ? values.address : undefined,
          placeOfBirth: "placeOfBirth" in values ? values.placeOfBirth : undefined,
          dateOfBirth: new Date(values.dateOfBirth).toString(),
          school: values.invoiceSchool,
          ico: "invoiceIco" in values ? values.invoiceIco : undefined,
          invoiceEmail: "invoiceEmail" in values ? values.invoiceEmail : undefined,
          paymentMethod: values.paymentMethod,
          note: values.note,
          fileUrls: "fileUrls" in values ? values.fileUrls : undefined,
          education: "education" in values ? values.education : undefined,
          wantsAllowance: "wantsAllowance" in values ? Boolean(values.wantsAllowance) : undefined,
          branchOffice: "branchOffice" in values ? values.branchOffice : undefined  ,
        },
      });
    },
    [seminar, mutate],
  );

  const isIdzSeminarType = useMemo(
    () => seminar?.type === SeminarType.Idz,
    [seminar],
  );
  const isDigCompEduType = useMemo(
    () => seminar?.type === SeminarType.Digcompedu,
    [seminar],
  );
  const isSeminarFullButSubstituesAllowed = useMemo(
    () =>
      Boolean(
        seminar?.capacity &&
          seminar?.substituteCapacity &&
          seminar?.registeredParticipants &&
          seminar.registeredParticipants >= seminar.capacity &&
          seminar.registeredParticipants <
            seminar.capacity + seminar.substituteCapacity,
      ),
    [seminar],
  );
  const isSeminarFullAlsoWithSubstitutes = useMemo(
    () =>
      Boolean(
        seminar?.capacity &&
          seminar?.registeredParticipants &&
          seminar.registeredParticipants >=
            seminar.capacity + (seminar.substituteCapacity || 0),
      ),
    [seminar],
  );

  return (
    <>
      <Head>
        <title>Registrace na seminář - Vysočina Education</title>
        <meta
          name="description"
          content="Registrace na seminář - Vysočina Education"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/logo.png" />
      </Head>
      <main className={styles.main}>
        <Box sx={{ maxWidth: '40rem', mb: 5 }}>
          <Block>
            <Image
              src={VysEduLogo}
              alt={'Vysočina Education'}
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          </Block>

          {isLoading ? (
            <Block>
              <Stack
                direction={'column'}
                gap={3}
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Načítám informace o semináři...
                </Typography>
              </Stack>
            </Block>
          ) : error ? (
            <Block>
              <Typography variant="body1" color="error">
                {error}
              </Typography>
            </Block>
          ) : seminar ? (
            <>
              <Block showTopBar>
                <Typography variant="h4" component="h1" sx={{ mb: '1rem' }}>
                  Přihláška na seminář{' '}
                  {isSeminarFullButSubstituesAllowed ? ' (jako náhradník)' : ''}
                </Typography>
                <Typography variant="h5" component="h2">
                  {seminar.name}
                </Typography>
                {isSeminarFullButSubstituesAllowed && !isRegistered && (
                  <Typography
                    variant="body1"
                    sx={{ my: '1rem', color: 'green' }}
                  >
                    Kapacita je už <strong>naplněna</strong>, ale můžete se
                    přihlásit jako náhradník. V případě uvolnění místa budete
                    kontaktováni.
                  </Typography>
                )}
                {isIdzSeminarType && (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontStyle: 'italic', mt: '1rem', mb: '0.5rem' }}
                      component="div"
                    >
                      Implementace dlouhodobého záměru Kraje Vysočina. Tento
                      projekt je spolufinancován Evropskou unií.
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontStyle: 'italic', mb: '1rem' }}
                      component="div"
                    >
                      CZ.02.02.XX/00/23_018/0010144
                    </Typography>
                  </>
                )}
                <Typography variant="subtitle1" component="div">
                  Datum: {formatDateWithTime(seminar.date)}{' '}
                  {seminar.endDate
                    ? ` - ${formatDateWithTime(seminar.endDate)}`
                    : ''}
                </Typography>
                {seminar.lector && (
                  <Typography variant="subtitle1" component="div">
                    Lektor: {seminar.lector}
                  </Typography>
                )}
                {seminar.duration && (
                  <Typography variant="subtitle1" component="div">
                    Počet hodin: {seminar.duration}
                  </Typography>
                )}
                {seminar.type === SeminarType.Studium && (
                  <>
                    {seminar.theoryDuration && (
                      <Typography variant="subtitle1" component="div">
                        Teorie: {seminar.theoryDuration}h
                      </Typography>
                    )}
                    {seminar.practiceDuration && (
                      <Typography variant="subtitle1" component="div">
                        Praxe: {seminar.practiceDuration}h
                      </Typography>
                    )}
                  </>
                )}
                {!isIdzSeminarType && (
                  <>
                    <Typography variant="subtitle1" component="div">
                      Cena: {seminar.price ?? 0}{' '}
                    </Typography>
                    {seminar.deposit && (
                      <Typography variant="subtitle1" component="div">
                        Záloha: {seminar.deposit}{' '}
                        {Number(seminar.deposit) >= 0 ? 'Kč' : 'Zdarma'}
                      </Typography>
                    )}
                  </>
                )}
                <Typography variant="subtitle1" component="div">
                  Místo: {seminar.city}
                </Typography>
              </Block>
              {isSeminarFullAlsoWithSubstitutes ? (
                <SeminarCapacityFull author={seminar.author} />
              ) : isRegistered ? (
                <RegistrationSuccessful email={participantEmail} />
              ) : (
                <RegisterForm
                  isSubmitting={isRegisterSubmitLoading}
                  onSubmit={submit}
                  seminarType={seminar.type}
                  isForFree={
                    !seminar.price ||
                    seminar.price === '' ||
                    seminar.price === '0' ||
                    seminar.price === 'Zdarma' ||
                    seminar.price === 'zdarma' ||
                    seminar.price === 'zadarmo'
                  }
                />
              )}
            </>
          ) : (
            <Block>
              Omlouváme se, ale seminář se nepodařilo načíst. Zkuste to prosím
              později
            </Block>
          )}
        </Box>
      </main>
    </>
  );
};

export default SeminarRegistration;
